import { AppConfig } from '@/config/appConfig';
import { getBrowserName } from '@/lib/commonHelpers';
import { getAppType, getAppVersion, isNewAppVersion } from '@/lib/offline/OfflineAppHelper';
import { offlineAppService } from '@/lib/offline/offlineAppService';

interface LogoutMessage {
  type: 'CROSS_APP_LOGOUT';
  source: 'v3';
}

export const broadcastLogoutToV2 = (): Promise<void> => {
  return new Promise<void>((resolve) => {
    const v2WebUrl = AppConfig.v2WebUrl;
    // const v2WebUrl = 'http://localhost:3000';

    // Create iframe dynamically when logout is called
    const iframe = document.createElement('iframe');
    iframe.src = `${v2WebUrl}/cross-app-logout`;
    iframe.style.display = 'none';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.title = 'Cross-app logout communication';

    // Cleanup function
    const cleanup = () => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
      // Remove the message event listener
      window.removeEventListener('message', messageHandler);
    };

    // Message handler for listening to V2 logout completion
    const messageHandler = (event: MessageEvent) => {
      // Verify the origin for security
      if (event.origin !== v2WebUrl) {
        return;
      }

      if (event.data?.type === 'V2_LOGOUT_COMPLETED') {
        console.log('V2 logout completed, cleaning up...');
        cleanup();
        resolve();
      }
    };

    // Add message event listener
    window.addEventListener('message', messageHandler);

    iframe.onerror = () => {
      console.error('Failed to load V2 logout iframe');
      cleanup();
      resolve();
    };

    // Fallback timeout in case V2 doesn't respond
    setTimeout(() => {
      console.warn('V2 logout timeout, cleaning up...');
      cleanup();
      resolve();
    }, 15000); // 15 second timeout

    // Append iframe to body
    document.body.appendChild(iframe);
  });
};

// ManageBac logout function
export const logoutFromMB = async (): Promise<void> => {
  console.log('logging out from mb==>');
  const appType = getAppType();
  const browserName = getBrowserName();
  const appVersion = getAppVersion(appType);

  try {
    if (appType === 'seb' || (appType === 'web' && browserName !== 'Safari')) {
      const formData = new FormData();
      formData.append('_method', 'delete');

      try {
        const response = await fetch(`${import.meta.env.VITE_MB_ACCOUNTS_URL}/accounts/sign-out`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        console.log('Logout of MB accounts success', response);
      } catch (error) {
        console.log('Logout of MB accounts error', error);
      }
    } else if (
      (appType === 'mac' && isNewAppVersion(appVersion, '9.0.1')) ||
      (appType === 'ios' && isNewAppVersion(appVersion, '2.0.5'))
    ) {
      offlineAppService.executeAppMethod({
        key: 'clearCacheWithSession',
        value: true,
      });
    } else if (appType === 'web' && browserName === 'Safari') {
      const title = 'Logout window';
      const logoutWindow = window.open(
        'about:blank',
        title,
        'toolbar=no,status=no,menubar=no,scrollbars=no,resizable=no,left=10000, top=10000, width=10, height=10, visible=none'
      );

      if (logoutWindow) {
        const form = document.createElement('form');
        form.action = `${import.meta.env.VITE_MB_ACCOUNTS_URL}/accounts/sign-out`;
        form.method = 'post';
        form.style.display = 'none';

        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        methodInput.value = 'delete';

        const submitInput = document.createElement('input');
        submitInput.type = 'submit';
        submitInput.value = 'Logout';

        form.appendChild(methodInput);
        form.appendChild(submitInput);
        logoutWindow.document.body.appendChild(form);

        form.submit();
        setTimeout(() => {
          logoutWindow.close();
        }, 1000);
      }
    }
  } catch (error) {
    console.log('error when logging out from managebac', error);
  }
};
